import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI, clientsAPI, catalogAPI } from '../services/api';
import { ShoppingCart, CheckCircle, XCircle, Search, Eye, User, Plus, Trash2 } from 'lucide-react';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import toast from 'react-hot-toast';

const PER_PAGE = 20;


function CreateOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ phone: '', hold_amount: '0', promo_code: '', external_order_id: '' });
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const t = setTimeout(() => {
      const url = productSearch
        ? `/catalog/products?skip=0&limit=50&search=${encodeURIComponent(productSearch)}`
        : `/catalog/products?skip=0&limit=50`;
      catalogAPI.products(0, 50, null, productSearch || null, false)
        .then(r => setProducts(r.data.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addItem = () => {
    setItems([...items, { product_id: '', name: '', price: 0, quantity: 1 }]);
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...items];
    newItems[idx][field] = value;
    if (field === 'product_id') {
      const prod = products.find(p => p.id === value);
      if (prod) {
        newItems[idx].name = prod.name;
        newItems[idx].price = prod.price || 0;
        newItems[idx]._product = prod;
      }
    }
    setItems(newItems);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const orderTotal = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (parseInt(i.quantity) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (orderTotal <= 0) { toast.error('Добавьте товары или укажите сумму'); return; }
    setLoading(true);
    try {
      await ordersAPI.create({
        phone: form.phone,
        order_total: orderTotal,
        hold_amount: parseFloat(form.hold_amount) || 0,
        promo_code: form.promo_code || null,
        external_order_id: form.external_order_id || null,
        items: items.length > 0 ? items.map(i => ({
          product_id: i.product_id || null,
          name: i.name,
          price: parseFloat(i.price) || 0,
          quantity: parseFloat(i.quantity) || 1,
        })) : null,
      });
      toast.success('Заказ создан');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <h2 className="modal-title">Новый заказ</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Телефон клиента *</label>
              <input className="form-input" required placeholder="+79001234567"
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Внешний ID заказа</label>
              <input className="form-input" placeholder="ORD-12345"
                value={form.external_order_id}
                onChange={e => setForm({ ...form, external_order_id: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Состав заказа</label>
              <button type="button" className="btn btn-ghost btn-sm" onClick={addItem}>
                <Plus size={14} /> Добавить товар
              </button>
            </div>
            {items.map((item, idx) => {
              const productOptions = products.map(p => ({
                value: p.id, label: p.name, sublabel: `${p.external_id} · ${p.price || 0} ₽`,
              }));
              // Гарантируем что выбранный товар всегда в опциях (берём из локального кэша item)
              if (item.product_id && !productOptions.find(o => o.value === item.product_id)) {
                productOptions.unshift({
                  value: item.product_id,
                  label: item._product?.name || item.name || 'Товар',
                  sublabel: item._product ? `${item._product.external_id} · ${item._product.price || 0} ₽` : '',
                });
              }
              return (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 70px 32px', gap: 8, marginBottom: 8, alignItems: 'start' }}>
                  <SearchableSelect
                    value={item.product_id}
                    onChange={v => updateItem(idx, 'product_id', v)}
                    onSearch={setProductSearch}
                    placeholder="Товар из каталога..."
                    options={productOptions}
                  />
                  <input className="form-input" type="number" placeholder="Цена" min="0" step="0.01"
                    value={item.price} onChange={e => updateItem(idx, 'price', e.target.value)} />
                  <input className="form-input" type="number" placeholder="Кол-во" min="1" step="1"
                    value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeItem(idx)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
            {items.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 12, textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)' }}>
                Без товаров — укажите общую сумму ниже
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Сумма заказа</label>
              <input className="form-input" type="number" min="0" step="0.01" required={items.length === 0}
                value={items.length > 0 ? orderTotal.toFixed(2) : (form.order_total || '')}
                disabled={items.length > 0}
                onChange={e => setForm({ ...form, order_total: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Списать баллов</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={form.hold_amount} onChange={e => setForm({ ...form, hold_amount: e.target.value })} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Промокод</label>
            <input className="form-input" placeholder="WELCOME"
              value={form.promo_code} onChange={e => setForm({ ...form, promo_code: e.target.value })} />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '...' : 'Создать заказ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function OrderDetailModal({ orderId, onClose, onChanged }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    ordersAPI.get(orderId).then(r => setOrder(r.data)).catch(() => toast.error('Ошибка загрузки')).finally(() => setLoading(false));
  }, [orderId]);

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await ordersAPI.confirm(orderId);
      toast.success('Заказ подтверждён');
      const r = await ordersAPI.get(orderId); setOrder(r.data);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setActionLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Отменить заказ?')) return;
    setActionLoading(true);
    try {
      await ordersAPI.cancel(orderId);
      toast.success('Заказ отменён');
      const r = await ordersAPI.get(orderId); setOrder(r.data);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setActionLoading(false); }
  };

  const goToClient = () => {
    window.open(`/clients?client_id=${order.client_id}`, '_blank');
  };

  if (loading) return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Загрузка...</div></div>
    </div>
  );

  if (!order) return null;

  const statusMap = {
    held: { text: 'Ожидает подтверждения', cls: 'badge-warning' },
    confirmed: { text: 'Подтверждён', cls: 'badge-success' },
    cancelled: { text: 'Отменён', cls: 'badge-danger' },
  };
  const stMeta = statusMap[order.status] || { text: order.status, cls: 'badge-info' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <h2 className="modal-title">Заказ #{order.id.slice(0, 8)}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, fontSize: 13 }}>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Статус</div>
            <span className={`badge ${stMeta.cls}`}>{stMeta.text}</span>
          </div>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Дата создания</div>
            <div>{new Date(order.created_at).toLocaleString('ru')}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Внешний ID</div>
            <div style={{ fontFamily: 'var(--font-mono)' }}>{order.external_order_id || '—'}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>Промокод</div>
            <div>{order.promo_code || '—'}</div>
          </div>
        </div>


        <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Клиент</div>
              <div style={{ fontWeight: 600 }}>{order.client_name || 'Без имени'}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{order.client_phone}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Баланс: {order.client_balance} ({order.client_held_balance} в холде)
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={goToClient}>
              <User size={14} /> Открыть карточку
            </button>
          </div>
        </div>


        {order.items && order.items.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Состав заказа</div>
            <table className="table" style={{ fontSize: 12, tableLayout: 'fixed', width: '100%' }}>
              <thead><tr>
                <th>Товар</th>
                <th style={{ textAlign: 'right', width: 90 }}>Цена</th>
                <th style={{ textAlign: 'right', width: 70 }}>Кол-во</th>
                <th style={{ textAlign: 'right', width: 100 }}>Сумма</th>
              </tr></thead>
              <tbody>
                {order.items.map(it => (
                  <tr key={it.id}>
                    <td style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {it.name}
                      {it.product_external_id && <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>ID: {it.product_external_id}</div>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{it.price.toLocaleString('ru')}</td>
                    <td style={{ textAlign: 'right' }}>{it.quantity}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{it.total.toLocaleString('ru')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}


        <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
            <span style={{ color: 'var(--text-muted)' }}>Сумма заказа:</span>
            <strong style={{ fontFamily: 'var(--font-mono)' }}>{order.order_total.toLocaleString('ru')} ₽</strong>
          </div>
          {order.hold_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
              <span style={{ color: 'var(--text-muted)' }}>Списано бонусов:</span>
              <strong style={{ fontFamily: 'var(--font-mono)' }}>{order.hold_amount.toLocaleString('ru')}</strong>
            </div>
          )}
          {order.cashback_amount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>Кешбэк:</span>
              <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--success)' }}>+{order.cashback_amount.toLocaleString('ru')}</strong>
            </div>
          )}
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>
          {order.status === 'held' && (
            <>
              <button className="btn btn-danger" disabled={actionLoading} onClick={handleCancel}>
                <XCircle size={14} /> Отменить
              </button>
              <button className="btn btn-success" disabled={actionLoading} onClick={handleConfirm}>
                <CheckCircle size={14} /> Подтвердить
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function ImportOrdersModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [accrueBonuses, setAccrueBonuses] = useState(false);
  const [defaultStatus, setDefaultStatus] = useState('confirmed');
  const [loading, setLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!taskId) return;
    const interval = setInterval(async () => {
      try {
        const res = await ordersAPI.importStatus(taskId);
        setProgress(res.data.progress);
        setStatus(res.data.status);
        if (res.data.status === 'success' || res.data.status === 'failed') {
          clearInterval(interval);
          setLoading(false);
          if (res.data.status === 'success') {
            toast.success(`Создано: ${res.data.progress?.created || 0}`);
            onImported();
          } else {
            toast.error(res.data.message || 'Ошибка');
          }
        }
      } catch { clearInterval(interval); setLoading(false); }
    }, 2000);
    return () => clearInterval(interval);
  }, [taskId]);

  const handleImport = async () => {
    if (!file) { toast.error('Выберите CSV файл'); return; }
    setLoading(true); setProgress(null); setStatus(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await ordersAPI.importCSV(formData, accrueBonuses, defaultStatus);
      setTaskId(res.data.task_id);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка импорта'); setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="modal-title">Импорт заказов из CSV</h2>
        <div className="form-group">
          <label className="form-label">CSV файл</label>
          <input type="file" accept=".csv,.txt,.tsv" onChange={e => setFile(e.target.files[0])}
            className="form-input" style={{ padding: 8 }} />
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.6 }}>
          Колонки: <code>телефон</code> (обяз.) · <code>сумма</code> (обяз.) · <code>дата покупки</code> · <code>магазин</code> · <code>номер заказа</code> · <code>промокод</code><br/>
          Дубли определяются по номеру заказа или по (клиент + сумма + дата). Точки продаж создаются автоматически.
        </div>
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Статус заказов по умолчанию</label>
          <select className="form-input" value={defaultStatus} onChange={e => setDefaultStatus(e.target.value)}>
            <option value="confirmed">Подтверждён</option>
            <option value="held">В ожидании</option>
            <option value="cancelled">Отменён</option>
          </select>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Применяется если в файле нет колонки «статус»</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16 }}>
          <input type="checkbox" checked={accrueBonuses} onChange={e => setAccrueBonuses(e.target.checked)} />
          <span style={{ fontSize: 13 }}>Начислять бонусы за заказы</span>
        </label>
        {(loading || (progress && status)) && (
          <div style={{ padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', marginBottom: 12, fontSize: 12 }}>
            {loading && <div style={{ marginBottom: 6 }}>{status === 'running' ? '⏳ Импорт выполняется...' : '⏳ Запуск...'}</div>}
            {!loading && status === 'success' && <div style={{ color: 'var(--success)', fontWeight: 600, marginBottom: 6 }}>Импорт завершён</div>}
            {!loading && status === 'failed' && <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 6 }}>Ошибка</div>}
            {progress && <>
              <div>Создано: <strong style={{ color: 'var(--success)' }}>{progress.created || 0}</strong></div>
              <div>Пропущено (дубли): <strong>{progress.skipped || 0}</strong></div>
              {progress.places_created > 0 && <div>Точек создано: <strong>{progress.places_created}</strong></div>}
              {progress.errors?.length > 0 && <div style={{ marginTop: 6, color: 'var(--danger)', fontSize: 11 }}>
                {progress.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
              </div>}
            </>}
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Закрыть</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={loading || !file}>
            {loading ? 'Импорт...' : 'Импортировать'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const clientFilterId = searchParams.get('client_id');

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [viewOrderId, setViewOrderId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const loadAll = (p = page, q = search) => {
    const skip = (p - 1) * PER_PAGE;
    ordersAPI.all(skip, PER_PAGE, q || null).then(res => {
      setOrders(res.data.items);
      setTotal(res.data.total);
    }).catch(() => {});
  };

  const loadByClientId = async (cid) => {
    try {
      const res = await ordersAPI.byClient(cid);
      setOrders(res.data);
      setTotal(res.data.length);
    } catch { toast.error('Ошибка загрузки'); }
  };

  useEffect(() => {
    if (clientFilterId) loadByClientId(clientFilterId);
    else loadAll(page, search);
  }, [page, clientFilterId]);


  useEffect(() => {
    if (clientFilterId) return;
    const t = setTimeout(() => {
      setPage(1);
      loadAll(1, search);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const totalPages = clientFilterId ? 1 : Math.ceil(total / PER_PAGE);

  const statusBadge = (status) => {
    const map = {
      held: { text: 'Ожидает', cls: 'badge-warning' },
      confirmed: { text: 'Подтверждён', cls: 'badge-success' },
      cancelled: { text: 'Отменён', cls: 'badge-danger' },
    };
    const m = map[status] || { text: status, cls: 'badge-info' };
    return <span className={`badge ${m.cls}`}>{m.text}</span>;
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Заказы</h1>
        <p className="page-subtitle">Всего: {total}{clientFilterId && ' (фильтр по клиенту)'}</p>
      </div>

      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <ShoppingCart size={16} /> Новый заказ
        </button>
        <button className="btn btn-ghost" onClick={() => setShowImport(true)}>
          Импорт CSV
        </button>
        <div style={{ flex: 1 }} />
        {clientFilterId ? (
          <button className="btn btn-ghost" onClick={() => navigate('/orders')}>
            Сбросить фильтр
          </button>
        ) : (
          <input className="form-input" style={{ maxWidth: 320 }}
            placeholder="Поиск по ID, телефону, имени, сумме..."
            value={search}
            onChange={e => setSearch(e.target.value)} />
        )}
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Внешний ID</th>
              <th>Клиент</th>
              <th>Сумма</th>
              <th>Товаров</th>
              <th>Списание</th>
              <th>Кешбэк</th>
              <th>Статус</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => setViewOrderId(o.id)}>
                <td style={{ fontSize: 12 }}>{new Date(o.created_at).toLocaleString('ru')}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{o.external_order_id || '—'}</td>
                <td>
                  {o.client_name && <div>{o.client_name}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{o.client_phone || '—'}</div>
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{o.order_total.toLocaleString('ru')} ₽</td>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{o.items_count || 0}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{o.hold_amount.toLocaleString('ru')}</td>
                <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--success)' }}>+{o.cashback_amount.toLocaleString('ru')}</td>
                <td>{statusBadge(o.status)}</td>
                <td>
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); setViewOrderId(o.id); }}>
                    <Eye size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {search ? 'Ничего не найдено' : 'Заказов пока нет'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {showCreate && <CreateOrderModal onClose={() => setShowCreate(false)} onCreated={() => loadAll(page)} />}
      {viewOrderId && <OrderDetailModal orderId={viewOrderId} onClose={() => setViewOrderId(null)} onChanged={() => loadAll(page)} />}
      {showImport && <ImportOrdersModal onClose={() => setShowImport(false)} onImported={() => loadAll(1)} />}
    </div>
  );
}